import React from 'react';
import { Package, Settings, CheckCircle, Snowflake, Truck, MapPin } from 'lucide-react';

const TraceabilityMap = ({ events, origin }) => {
  const eventIcons = {
    catch: Package,
    processing: Settings,
    quality_check: CheckCircle,
    storage: Snowflake,
    transport: Truck,
    delivery: MapPin,
  };

  const eventColors = {
    catch: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', icon: 'text-blue-600' },
    processing: { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-700', icon: 'text-amber-600' },
    quality_check: { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-700', icon: 'text-emerald-600' },
    storage: { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-700', icon: 'text-indigo-600' },
    transport: { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-700', icon: 'text-pink-600' },
    delivery: { bg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-700', icon: 'text-teal-600' },
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NO', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getEventLabel = (type) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="w-full" data-testid="traceability-map">
      {/* Horizontal Schematic Timeline */}
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Supply Chain Route</h3>
          <p className="text-sm text-slate-600">
            {events.length} verified checkpoints from {origin.name} to final destination
          </p>
        </div>

        {/* Timeline nodes */}
        <div className="relative">
          {/* Horizontal connecting line */}
          <div className="absolute top-12 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-teal-200" 
               style={{ width: 'calc(100% - 48px)', left: '24px' }}></div>

          {/* Event nodes */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${events.length}, 1fr)` }}>
            {events.map((event, idx) => {
              const Icon = eventIcons[event.event_type] || Package;
              const colors = eventColors[event.event_type] || eventColors.catch;
              const isFirst = idx === 0;
              const isLast = idx === events.length - 1;

              return (
                <div key={idx} className="flex flex-col items-center relative">
                  {/* Node circle */}
                  <div className={`w-12 h-12 rounded-full ${colors.bg} border-4 ${colors.border} flex items-center justify-center z-10 mb-3`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>

                  {/* Verification badge */}
                  {event.verified && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center z-20">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  )}

                  {/* Event details card */}
                  <div className={`w-full p-4 rounded-lg border-2 ${colors.border} ${colors.bg} text-center`}>
                    <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${colors.text}`}>
                      Step {idx + 1}
                    </div>
                    <div className="text-sm font-bold text-slate-900 mb-1">
                      {getEventLabel(event.event_type)}
                    </div>
                    <div className="text-xs text-slate-700 font-medium mb-2">
                      {event.location.name}
                    </div>
                    <div className="text-xs text-slate-500 mb-2">
                      {event.location.region}
                    </div>
                    <div className="text-xs text-slate-400">
                      {formatDate(event.timestamp)}
                    </div>
                    {event.temperature && (
                      <div className="mt-2 pt-2 border-t border-slate-200">
                        <div className="text-xs text-slate-600">
                          Temp: {event.temperature}°C
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Operator info */}
                  <div className="mt-2 text-xs text-slate-500 text-center">
                    {event.operator}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary footer */}
        <div className="mt-8 pt-6 border-t border-slate-200">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span className="font-medium text-slate-900">
                All {events.length} checkpoints verified and blockchain-recorded
              </span>
            </div>
            <div className="text-slate-600">
              Origin: {origin.name}, {origin.region}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 bg-slate-50 rounded-lg p-6 border border-slate-200">
        <h4 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">
          Event Types
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(eventIcons).map(([type, Icon]) => {
            const colors = eventColors[type];
            return (
              <div key={type} className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${colors.icon}`} />
                </div>
                <span className="text-sm text-slate-700 capitalize">
                  {getEventLabel(type)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TraceabilityMap;
